import { createContext, useContext, type ReactNode } from 'react';

interface InspectorNodeContextValue {
  /** The node ID that inspector controls should target */
  targetNodeId: string | null;
}

const InspectorNodeContext = createContext<InspectorNodeContextValue>({ targetNodeId: null });

interface InspectorNodeProviderProps {
  /** The node ID that inspector controls should target */
  nodeId: string | null | undefined;
  children: ReactNode;
}

/**
 * Provider that allows inspector field components to target a specific node
 * regardless of what node is currently selected in the editor.
 *
 * This is useful for composite components like Form where the inspector
 * should modify the Form node even when a child field is selected.
 */
export const InspectorNodeProvider = ({ nodeId, children }: InspectorNodeProviderProps) => {
  return (
    <InspectorNodeContext.Provider value={{ targetNodeId: nodeId ?? null }}>
      {children}
    </InspectorNodeContext.Provider>
  );
};

/**
 * Hook to get the target node ID from the InspectorNodeContext.
 * Returns null if no context is provided (fallback behavior).
 */
export const useInspectorNodeContext = () => {
  return useContext(InspectorNodeContext);
};

export default InspectorNodeContext;
